import { hasActiveFilters } from '@hrms/shared'
import {
  LuChevronLeft,
  LuChevronRight,
  LuColumns3,
  LuEyeOff,
  LuRefreshCw,
  LuSearchX,
  LuTicket,
} from 'react-icons/lu'
import { Link } from 'react-router'
import { Button, EmptyState } from '../../../components/ui/index.ts'
import { useConfig } from '../../../lib/config.ts'
import { hint } from '../../../lib/hint.ts'
import { useNow } from '../../../lib/useNow.ts'
import { QueueFilterBar } from '../queue/QueueFilterBar.tsx'
import { QueueSkeleton, QueueTable } from '../queue/QueueTable.tsx'
import { QueueTiles } from '../queue/QueueTiles.tsx'
import { boardSearchOf, useQueueParams } from '../queue/useQueueParams.ts'
import { useDirectory, useQueue } from '../tickets/api.ts'
import { useOpenTicket } from '../tickets/useTicketParam.ts'
import { PageHeader } from './PageHeader.tsx'

const ticketsWord = (n: number) => (n === 1 ? 'ticket' : 'tickets')

/** HR workspace: count line, tiles, filters, sortable table (DESIGN.md §3.1). */
export function TicketsPage() {
  const params = useQueueParams()
  const { query, filters, search } = params
  const queue = useQueue(search)
  const { data: config } = useConfig()
  const directory = useDirectory()
  const now = useNow()
  const openTicket = useOpenTicket()
  const data = queue.data
  const filtered = hasActiveFilters(filters)
  // The board splits by status itself and has no sort or pages.
  const boardSearch = boardSearchOf(search)

  const countLine = data
    ? `${data.total} of ${data.overall} ${ticketsWord(data.overall)}${filtered ? ' match your filters' : ''}`
    : 'Loading tickets…'

  let body: React.ReactNode
  if (queue.isError && !data)
    body = (
      <EmptyState
        icon={LuRefreshCw}
        color="amber"
        title="Couldn’t load tickets"
        action={<Button onClick={() => void queue.refetch()}>Try again</Button>}
      >
        Check your connection and try again.
      </EmptyState>
    )
  else if (!data || !config) body = <QueueSkeleton />
  else if (data.overall === 0)
    body = (
      <EmptyState icon={LuTicket} color="violet" title="No tickets yet">
        Tickets employees raise will show up here.
      </EmptyState>
    )
  else if (data.total === 0)
    body = (
      <EmptyState
        icon={LuSearchX}
        color="slate"
        title="No tickets match these filters"
        action={<Button onClick={params.clearFilters}>Clear filters</Button>}
      >
        Try widening the date range or removing a filter.
      </EmptyState>
    )
  else {
    const pages = Math.max(1, Math.ceil(data.total / data.pageSize))
    const first = (data.page - 1) * data.pageSize + 1
    const last = Math.min(data.total, data.page * data.pageSize)
    body = (
      <QueueTable
        tickets={data.tickets}
        people={data.people}
        config={config}
        now={now}
        sort={query.sort ?? 'created'}
        dir={query.dir ?? (query.sort === 'eta' ? 'asc' : 'desc')}
        onSort={params.setSort}
        onOpen={openTicket}
        loading={queue.isPlaceholderData}
        footer={
          <nav
            aria-label="Pages"
            className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-surface px-3 py-2 text-[13px] text-muted"
          >
            <span className="tabular">
              {data.tickets.length === 0
                ? `No tickets on page ${data.page}`
                : `Showing ${first}–${last} of ${data.total}`}
            </span>
            {pages > 1 && (
              <span className="flex items-center gap-2">
                <span className="tabular">
                  Page {data.page} of {pages}
                </span>
                <Button
                  size="sm"
                  aria-label="Previous page"
                  disabled={data.page <= 1}
                  onClick={() => params.setPage(data.page - 1)}
                  {...hint('Previous page')}
                >
                  <LuChevronLeft aria-hidden className="size-4" />
                </Button>
                <Button
                  size="sm"
                  aria-label="Next page"
                  disabled={data.page >= pages}
                  onClick={() => params.setPage(data.page + 1)}
                  {...hint('Next page')}
                >
                  <LuChevronRight aria-hidden className="size-4" />
                </Button>
              </span>
            )}
          </nav>
        }
      />
    )
  }

  const hidden = data?.hiddenConfidential ?? 0
  return (
    <>
      <PageHeader
        title="Tickets"
        description={countLine}
        actions={
          <Link
            to={{ pathname: '/hr/board', search: boardSearch ? `?${boardSearch}` : '' }}
            data-tour="view-switch"
            className="inline-flex h-[34px] items-center gap-1.5 rounded-ctl border border-border-strong bg-surface px-3.5 font-medium hover:bg-surface-2"
            {...hint('Same filters, one column per status')}
          >
            <LuColumns3 aria-hidden className="size-4 text-teal-fg" />
            Board view
          </Link>
        }
      />
      <QueueTiles
        tiles={data?.tiles}
        filters={filters}
        setFilters={params.setFilters}
        statuses={config?.statuses ?? []}
      />
      {config && (
        <QueueFilterBar
          filters={filters}
          setFilters={params.setFilters}
          clearFilters={params.clearFilters}
          config={config}
          people={directory.data?.people ?? []}
          timeZone={directory.data?.timeZone}
          now={now}
        />
      )}
      {hidden > 0 && (
        <p className="mb-3 flex items-center gap-1.5 text-[13px] text-muted">
          <LuEyeOff aria-hidden className="size-4 shrink-0 text-amber-fg" />
          {hidden === 1
            ? '1 confidential ticket is hidden. Only HR leads and the assigned HR partner can see it.'
            : `${hidden} confidential tickets are hidden. Only HR leads and the assigned HR partner can see them.`}
        </p>
      )}
      {body}
    </>
  )
}
